import { map, range, filter, isNil, isEmpty } from 'ramda';
import i18n from 'helpers/i18n';
import messages from './messages';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { getGroupBaseColor } from 'helpers/ageLevel';
import trainingValidation from './trainingValidation';
import { useStates } from 'components/dropdowns/States';
import SearchAccount from 'components/TrainingCatalog/Create/SearchAccount';
import { useCountries } from 'components/dropdowns/Countries';
import usePermissions from 'hooks/usePermissions';
import useCurrentUser from 'hooks/useCurrentUser';
import { Formik } from 'components/EnhancedForm';
import RichTextEditor from 'components/RichTextEditor';
import {
  Alert,
  Button,
  Combobox,
  ComboboxOption,
  DatePicker,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  RadioGroup,
  Text,
  TextField
} from '@teachstonellc/component-library';
import {
  AlertTriangleFilled,
  AvatarFilled,
  Bookmark,
  Calendar,
  CaretDown,
  Clock,
  Compass,
  ListTodo
} from '@teachstonellc/component-library/dist/icons';
import { Training } from 'types/api/Training';
import { TrainingTemplate } from 'types/api/TrainingTemplate';
import { CenterSelect } from 'components/TrainingCatalog/Create/CenterSelect';
import { FormikErrors, useFormikContext } from 'formik';
import { getEligibleTrainers } from 'actions/templates';
import { Trainer } from 'types/api/Trainer';
import { useGetUserAccounts } from 'actions/userAccounts';

import {
  formatSession,
  sessionIsValid,
  initialEditValues,
  initialCreateValues,
  URLState,
  getTimePickerOptions,
  getTimeZoneOptions,
  optionForValue,
  optionsWithValue
} from './utils';

import { useLocationState } from 'hooks/useLocationState';

const VIRTUAL_FORMAT = 'virtual';
const IN_PERSON_FORMAT = 'in_person';
const SESSION_DATE_FORMAT = 'MM/dd/yyyy';
const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]):not([disabled]), button:not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="combobox"]:not([disabled])';

function formatSessionDate(date: Date | undefined): string {
  if (!date) {
    return '';
  }

  return DateTime.fromJSDate(date).toFormat(SESSION_DATE_FORMAT);
}

function convertStringToDate(dateString?: string): Date | undefined {
  if (!dateString) return undefined;

  const parsed = DateTime.fromFormat(dateString, SESSION_DATE_FORMAT);

  return parsed.isValid ? parsed.toJSDate() : undefined;
}

interface TrainingFormProps {
  edit: boolean;
  training: Training;
  template: TrainingTemplate;
  onSubmit: (
    training: Training,
    setSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    resetForm?: () => void
  ) => void;
  onCancel: () => void;
  submitLabel: string;
}

type SetFieldValue = (
  field: string,
  value: any,
  shouldValidate?: boolean
) => Promise<void | FormikErrors<any>>;
type SubmitForm = () => Promise<void>;

// Mirrors the slice of Formik's touched shape this helper reads, without
// importing FormikTouched itself. Remove when we refactor to react-hook-form
type SessionsTouched = {
  sessions?: Array<{ start_time?: boolean; end_time?: boolean } | undefined>;
};

function sessionTimeError(
  errors: FormikErrors<any>,
  touched: SessionsTouched,
  index: number,
  field: 'start_time' | 'end_time'
): string | undefined {
  const touchedSessions = touched.sessions;
  const errorSessions = errors.sessions;

  if (!Array.isArray(touchedSessions) || !touchedSessions[index]?.[field]) {
    return undefined;
  }

  if (!Array.isArray(errorSessions)) {
    return undefined;
  }

  const sessionError = errorSessions[index];
  if (
    sessionError &&
    typeof sessionError === 'object' &&
    typeof sessionError[field] === 'string'
  ) {
    return sessionError[field];
  }

  return undefined;
}

function comboboxValue(option: ComboboxOption | null): string {
  return option == null ? '' : String(option.value);
}

// Formik doesn't move focus on a failed submit, so once a submit attempt
// leaves the form invalid, focus the first field marked aria-invalid.
//
// submitCount increments synchronously, but errors/isValid resolve after
// validation's promise settles, often in a later render. Depending on both
// and gating with a "resolved for this submitCount" ref lets the effect keep
// re-checking across that gap without refiring on ordinary typing once this
// submit attempt has already been handled.
function FocusFirstInvalidField({
  formRef
}: {
  formRef: React.RefObject<HTMLFormElement>;
}) {
  const { submitCount, isValid } = useFormikContext();
  const resolvedSubmitCount = useRef(0);

  useEffect(() => {
    if (submitCount === 0) {
      resolvedSubmitCount.current = 0;
      return;
    }

    if (submitCount === resolvedSubmitCount.current) {
      return;
    }

    if (isValid) {
      return;
    }

    const firstInvalid = formRef.current?.querySelector<HTMLElement>(
      '[aria-invalid="true"]'
    );

    if (!firstInvalid) {
      return;
    }

    firstInvalid.focus();
    resolvedSubmitCount.current = submitCount;
  }, [submitCount, isValid]);

  return null;
}

// A Combobox's typed query is local state, so resetForm (e.g. the "Create &
// Duplicate" flow in Create.js) clears the underlying Formik value without
// clearing what's still shown in the input. Resyncing on every change to
// `value`, not just the ones this component's own onChange produces, keeps
// the displayed text from going stale after an external reset.
function SyncComboboxQuery({
  value,
  label,
  setQuery
}: {
  value: unknown;
  label: string;
  setQuery: (query: string) => void;
}) {
  useEffect(() => {
    setQuery(label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return null;
}

function SectionHeader({
  icon: Icon,
  children
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 mb-3 text-[#006699]">
      <Icon size={18} />
      <Text as="h3" className="!text-[#006699] uppercase font-bold text-base">
        {children}
      </Text>
    </div>
  );
}

function TrainingForm({
  edit,
  template,
  training,
  onCancel,
  onSubmit,
  submitLabel
}: TrainingFormProps): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [selectedTrainerTypeId, setSelectedTrainerTypeId] = useState<
    number | undefined
  >();
  const trainingPermissions = usePermissions('trainer_dashboard', 'trainings');
  const currentUser = useCurrentUser();
  const canManageTrainings =
    trainingPermissions.manage || trainingPermissions.create;
  const { data: userAccounts, isPending: userAccountsAreLoading } =
    useGetUserAccounts(canManageTrainings ? undefined : currentUser.id);
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [trainersIsLoading, setTrainersIsLoading] = useState(false);
  const locationState = useLocationState<URLState>();
  const [timeZoneQuery, setTimeZoneQuery] = useState('');
  const [startTimeQueries, setStartTimeQueries] = useState<
    Record<number, string>
  >({});
  const [endTimeQueries, setEndTimeQueries] = useState<Record<number, string>>(
    {}
  );
  const timeZoneOptions = useMemo(() => getTimeZoneOptions(), []);
  const timeOptions = useMemo(() => getTimePickerOptions(15), []);
  const countries = useCountries();
  const states = useStates();
  const countryOptions = useMemo(
    () =>
      countries.map(c => ({
        label: c.text,
        value: c.value
      })),
    [countries]
  );
  const stateOptions = useMemo(
    () =>
      states.map(s => ({
        label: s.text,
        value: s.value
      })),
    [states]
  );
  const [countryQuery, setCountryQuery] = useState('');
  const [stateQuery, setStateQuery] = useState('');
  const [trainerQuery, setTrainerQuery] = useState('');
  const trainerOptions = useMemo(
    () =>
      trainers.map(trainer => ({
        label: `${trainer.first_name} ${trainer.last_name}`,
        value: trainer.id
      })),
    [trainers]
  );
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!canManageTrainings) {
      return;
    }

    setTrainersIsLoading(true);
    getEligibleTrainers(template.id).then(({ data }) => {
      setTrainers(data);
      setTrainersIsLoading(false);
    });
  }, [canManageTrainings, template.id]);

  const handleTrainerSelect = (trainerId: number) => {
    const selectedTrainer = trainers.find(trainer => trainer.id === trainerId);

    if (selectedTrainer) {
      setSelectedTrainerTypeId(selectedTrainer.type_id);
    }
  };

  // After a successful "Create & Duplicate", the form resets in place
  // rather than navigating away, so nothing else moves focus back to a
  // sensible starting point.
  function focusFirstEditableField() {
    formRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
  }

  function handleSubmit({ sessions, ...values }: any, { resetForm }: any) {
    if (submitting) {
      return;
    }

    const trainingSessions = map(
      (session: any) => formatSession(session, values.time_zone),
      filter(sessionIsValid, sessions)
    );

    let training = {
      training_template_id: template.id,
      sessions: trainingSessions,
      ...values
    };

    // trainer is only set when an user can manage trainings
    // otherwise the current user is the trainer
    if (isNil(training.trainer_id) || isEmpty(training.trainer_id)) {
      training.trainer_id = currentUser.id;
    }

    const resetFormAndFocusFirstField: typeof resetForm = (...args: any[]) => {
      resetForm(...args);
      focusFirstEditableField();
    };

    setSubmitting(true);
    onSubmit(training, setSubmitting, resetFormAndFocusFirstField);
  }

  // Require selecting a trainer only when the Eligible Trainers dropdown is shown.
  function trainerRequired() {
    return canManageTrainings;
  }

  const affiliateAccounts = userAccounts?.accounts ?? [];
  const showTrainerOrganizationSection =
    canManageTrainings ||
    (!userAccountsAreLoading &&
      (affiliateAccounts.length > 0 || Boolean(edit && training?.account)));

  function getInitialValues() {
    return edit
      ? initialEditValues(template, training)
      : initialCreateValues(template, locationState ?? {});
  }

  const isInPerson = ({ delivery_method }: any) =>
    delivery_method === IN_PERSON_FORMAT;

  const submitOptions = (
    setFieldValue: SetFieldValue,
    submitForm: SubmitForm
  ) => [
    {
      key: 'duplicate',
      text: i18n.ft(messages.createDuplicate),
      value: 'duplicate',
      onClick: () => {
        setFieldValue('submit_type', 'duplicate').then(() => submitForm());
      }
    },
    {
      key: 'new',
      text: i18n.ft(messages.createNew),
      value: 'new',
      onClick: () => {
        setFieldValue('submit_type', 'new').then(() => submitForm());
      }
    }
  ];

  return (
    <Formik
      onSubmit={handleSubmit}
      validate={trainingValidation(trainerRequired())}
      initialValues={getInitialValues()}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        isValid,
        submitForm,
        handleSubmit,
        handleReset
      }) => (
        <form
          id="training-form"
          ref={formRef}
          onSubmit={handleSubmit}
          onReset={handleReset}
        >
          <FocusFirstInvalidField formRef={formRef} />
          <div className="p-4 pb-2.5 mb-4 rounded bg-white shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
            <div
              className="h-[5px] -mt-4 -mx-4 mb-3 rounded-t"
              style={{
                backgroundColor: getGroupBaseColor(template.age_levels)
              }}
            />
            <div
              className="h-[40vw] max-h-[350px] mb-4 bg-gray-400 bg-cover bg-center bg-no-repeat"
              style={
                template.banner
                  ? { backgroundImage: `url(${template.banner})` }
                  : undefined
              }
            />
            <Text as="h2" variant="h2" className="!text-[#006699] font-bold">
              {template.title}
            </Text>
          </div>

          {edit && <input type="hidden" name="id" value={(values as any).id} />}

          {!edit && (
            <div className="mb-4">
              <Alert
                title={i18n.ft(messages.maxParticipantsWarningLabel, {
                  max_participants: template.max_participants
                })}
                description={i18n.ft(
                  messages.maxParticipantsWarningDescription,
                  {
                    max_participants: template.max_participants
                  }
                )}
                variant="notice"
              />
            </div>
          )}

          <div className="p-4 pb-2.5 mb-4 rounded bg-white shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
            <SectionHeader icon={Calendar}>
              {i18n.ft(messages.sessionDates)}
            </SectionHeader>

            <div
              className="w-full md:w-[calc((100%-32px)/2)] mb-4"
              onBlur={() => setFieldTouched('time_zone', true)}
            >
              <Combobox
                label={i18n.ft(messages.timeZone)}
                options={optionsWithValue(timeZoneOptions, values.time_zone)}
                selectedOption={optionForValue(
                  timeZoneOptions,
                  values.time_zone
                )}
                onChange={option => {
                  setFieldValue('time_zone', comboboxValue(option));
                  setTimeZoneQuery(option?.label ?? '');
                }}
                query={timeZoneQuery}
                setQuery={setTimeZoneQuery}
                showDropdownIcon
                maxResults={100}
                immediate={false}
              />
            </div>

            {map(
              nSession => {
                const startTime = values.sessions?.[nSession]?.start_time;
                const endTime = values.sessions?.[nSession]?.end_time;
                const sessionDateField = `sessions.${nSession}.date`;
                const sessionDateTouched = (
                  touched.sessions?.[nSession] as { date?: boolean } | undefined
                )?.date;
                const sessionDateError = (
                  errors.sessions?.[nSession] as { date?: string } | undefined
                )?.date;

                return (
                  <div
                    className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4"
                    key={nSession}
                  >
                    {edit && (
                      <input
                        type="hidden"
                        name={`sessions.${nSession}.id`}
                        value={(values.sessions?.[nSession] as any)?.id}
                      />
                    )}
                    <div className="[&>div]:items-stretch">
                      <DatePicker
                        label={i18n.ft(messages.sessionNumber, {
                          number: nSession + 1
                        })}
                        placeholder="MM/DD/YYYY"
                        value={convertStringToDate(
                          values.sessions[nSession]?.date
                        )}
                        onChange={(date: Date | undefined) => {
                          setFieldValue(
                            sessionDateField,
                            formatSessionDate(date)
                          );
                          setFieldTouched(sessionDateField, true, false);
                        }}
                        error={
                          sessionDateTouched ? sessionDateError : undefined
                        }
                        errorId={`session-${nSession + 1}-date-error`}
                      />
                    </div>

                    <div
                      onBlur={() =>
                        setFieldTouched(`sessions.${nSession}.start_time`, true)
                      }
                    >
                      <Combobox
                        label={i18n.ft(messages.startTime)}
                        placeholder={i18n.ft(messages.startTime)}
                        options={optionsWithValue(timeOptions, startTime)}
                        selectedOption={optionForValue(timeOptions, startTime)}
                        onChange={option => {
                          setFieldValue(
                            `sessions.${nSession}.start_time`,
                            comboboxValue(option)
                          );
                          setStartTimeQueries(queries => ({
                            ...queries,
                            [nSession]: option?.label ?? ''
                          }));
                        }}
                        query={startTimeQueries[nSession] ?? ''}
                        setQuery={query =>
                          setStartTimeQueries(queries => ({
                            ...queries,
                            [nSession]: query
                          }))
                        }
                        showSearchIcon={false}
                        showDropdownIcon
                        startIcon={Clock}
                        maxResults={timeOptions.length}
                        immediate={false}
                        error={sessionTimeError(
                          errors,
                          touched,
                          nSession,
                          'start_time'
                        )}
                      />
                    </div>

                    <div
                      onBlur={() =>
                        setFieldTouched(`sessions.${nSession}.end_time`, true)
                      }
                    >
                      <Combobox
                        label={i18n.ft(messages.endTime)}
                        placeholder={i18n.ft(messages.endTime)}
                        options={optionsWithValue(timeOptions, endTime)}
                        selectedOption={optionForValue(timeOptions, endTime)}
                        onChange={option => {
                          setFieldValue(
                            `sessions.${nSession}.end_time`,
                            comboboxValue(option)
                          );
                          setEndTimeQueries(queries => ({
                            ...queries,
                            [nSession]: option?.label ?? ''
                          }));
                        }}
                        query={endTimeQueries[nSession] ?? ''}
                        setQuery={query =>
                          setEndTimeQueries(queries => ({
                            ...queries,
                            [nSession]: query
                          }))
                        }
                        showSearchIcon={false}
                        showDropdownIcon
                        startIcon={Clock}
                        maxResults={timeOptions.length}
                        immediate={false}
                        error={sessionTimeError(
                          errors,
                          touched,
                          nSession,
                          'end_time'
                        )}
                      />
                    </div>
                  </div>
                );
              },
              range(0, template.number_of_sessions || 0)
            )}
          </div>

          {showTrainerOrganizationSection && (
            <div className="p-4 pb-2.5 mb-4 rounded bg-white shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
              <SectionHeader icon={AvatarFilled}>
                {i18n.ft(
                  canManageTrainings
                    ? messages.selectTrainer
                    : messages.selectOrganization
                )}
              </SectionHeader>

              <div className="grid md:grid-cols-3 md:gap-4">
                {canManageTrainings && (
                  <div onBlur={() => setFieldTouched('trainer_id', true)}>
                    <SyncComboboxQuery
                      value={values.trainer_id}
                      label={
                        trainerOptions.find(
                          option => option.value === values.trainer_id
                        )?.label ?? ''
                      }
                      setQuery={setTrainerQuery}
                    />
                    <Combobox
                      label={i18n.ft(messages.eligibleTrainers)}
                      placeholder={i18n.ft(messages.eligibleTrainers)}
                      options={trainerOptions}
                      selectedOption={
                        trainerOptions.find(
                          option => option.value === values.trainer_id
                        ) ?? null
                      }
                      onChange={option => {
                        setFieldValue('trainer_id', option?.value ?? null);

                        if (option) {
                          handleTrainerSelect(option.value as number);
                        }
                      }}
                      query={trainerQuery}
                      setQuery={setTrainerQuery}
                      disabled={trainersIsLoading}
                      showSearchIcon={false}
                      showDropdownIcon
                      maxResults={100}
                      immediate={false}
                      error={
                        touched.trainer_id &&
                        typeof errors.trainer_id === 'string'
                          ? errors.trainer_id
                          : undefined
                      }
                    />
                  </div>
                )}

                <SearchAccount
                  name="account_id"
                  label={i18n.ft(messages.client_organization)}
                  initialAccount={training?.account}
                  trainerId={values.trainer_id}
                  selectedTrainerTypeId={selectedTrainerTypeId}
                  availableAccounts={
                    canManageTrainings ? undefined : affiliateAccounts
                  }
                />

                {/* readOnly serves to keep the historical Center visible without fetching an account the trainer no longer belongs to. */}
                <CenterSelect
                  readOnly={
                    !canManageTrainings &&
                    edit &&
                    'account_id' in values &&
                    values.account_id === training?.account?.id &&
                    !affiliateAccounts.some(
                      account => account.id === values.account_id
                    )
                  }
                  savedCenter={training?.node}
                />
              </div>
            </div>
          )}

          <div className="p-4 pb-2.5 mb-4 rounded bg-white shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
            <SectionHeader icon={ListTodo}>
              {i18n.ft(messages.trainingDeliveryFormat)}
            </SectionHeader>
            <RadioGroup
              name="delivery_method"
              label={i18n.ft(messages.trainingDeliveryFormat)}
              showLabel={false}
              required
              value={values.delivery_method}
              onChange={value => setFieldValue('delivery_method', value)}
              error={
                touched.delivery_method &&
                typeof errors.delivery_method === 'string'
                  ? errors.delivery_method
                  : undefined
              }
              options={[
                {
                  label: i18n.ft(messages.virtualDeliveryMethod),
                  value: VIRTUAL_FORMAT
                },
                {
                  label: i18n.ft(messages.inPersonDeliveryMethod),
                  value: IN_PERSON_FORMAT
                }
              ]}
            />
          </div>

          <div className="p-4 pb-2.5 mb-4 rounded bg-white shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
            <SectionHeader icon={Compass}>
              {i18n.ft(messages.trainingLocation)}
            </SectionHeader>

            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <TextField
                  name="location_address_1"
                  label={i18n.ft(messages.address, { number: 1 })}
                  placeholder={i18n.ft(messages.address01)}
                  value={values.location_address_1 ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.location_address_1 && errors.location_address_1
                      ? (errors.location_address_1 as string)
                      : undefined
                  }
                  required={isInPerson(values)}
                />
                <TextField
                  name="location_address_2"
                  label={i18n.ft(messages.address, { number: 2 })}
                  placeholder={i18n.ft(messages.address02)}
                  value={values.location_address_2 ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.location_address_2 && errors.location_address_2
                      ? (errors.location_address_2 as string)
                      : undefined
                  }
                />
                <TextField
                  name="location_city"
                  label={i18n.ft(messages.city)}
                  placeholder={i18n.ft(messages.city)}
                  value={values.location_city ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.location_city && errors.location_city
                      ? (errors.location_city as string)
                      : undefined
                  }
                  required={isInPerson(values)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <Combobox
                  label={i18n.ft(messages.country)}
                  placeholder={i18n.ft(messages.country)}
                  options={optionsWithValue(
                    countryOptions,
                    values.location_country
                  )}
                  selectedOption={optionForValue(
                    countryOptions,
                    values.location_country
                  )}
                  onChange={option => {
                    const val = comboboxValue(option);
                    setFieldValue('location_country', val);
                    setCountryQuery(option?.label ?? '');
                    if (val !== 'US') {
                      setFieldValue('location_state', '');
                      setStateQuery('');
                    }
                  }}
                  query={countryQuery}
                  setQuery={setCountryQuery}
                  showDropdownIcon
                  maxResults={100}
                  immediate={false}
                  error={
                    touched.location_country && errors.location_country
                      ? (errors.location_country as string)
                      : undefined
                  }
                  required={isInPerson(values)}
                />

                {values.location_country === 'US' ? (
                  <Combobox
                    label={i18n.ft(messages.stateProvince)}
                    placeholder={i18n.ft(messages.stateProvince)}
                    options={optionsWithValue(
                      stateOptions,
                      values.location_state
                    )}
                    selectedOption={optionForValue(
                      stateOptions,
                      values.location_state
                    )}
                    onChange={option => {
                      setFieldValue('location_state', comboboxValue(option));
                      setStateQuery(option?.label ?? '');
                    }}
                    query={stateQuery}
                    setQuery={setStateQuery}
                    showDropdownIcon
                    maxResults={100}
                    immediate={false}
                    error={
                      touched.location_state && errors.location_state
                        ? (errors.location_state as string)
                        : undefined
                    }
                    required={isInPerson(values)}
                  />
                ) : (
                  <TextField
                    name="location_state"
                    label={i18n.ft(messages.stateProvince)}
                    placeholder={i18n.ft(messages.stateProvince)}
                    value={values.location_state ?? ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.location_state && errors.location_state
                        ? (errors.location_state as string)
                        : undefined
                    }
                    required={isInPerson(values)}
                  />
                )}

                <TextField
                  name="location_zip"
                  label={i18n.ft(messages.postalCode)}
                  placeholder={i18n.ft(messages.postalCode)}
                  value={values.location_zip ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.location_zip && errors.location_zip
                      ? (errors.location_zip as string)
                      : undefined
                  }
                  required={isInPerson(values)}
                />
              </div>
              <TextField
                className="w-full md:w-1/2 md:min-w-[480px]"
                name="virtual_training_link"
                label={i18n.ft(messages.virtualTrainingLink)}
                placeholder={i18n.ft(messages.virtualTrainingLinkHint)}
                value={values.virtual_training_link ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.virtual_training_link && errors.virtual_training_link
                    ? (errors.virtual_training_link as string)
                    : undefined
                }
              />
            </div>
          </div>

          <div className="p-4 pb-2.5 mb-4 rounded bg-white shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
            <SectionHeader icon={Bookmark}>
              {i18n.ft(messages.additionalInfo)}
            </SectionHeader>

            <div className="flex flex-col gap-4">
              <TextField
                className="w-full md:w-1/2 md:min-w-[480px]"
                name="notes_title"
                label={i18n.ft(messages.notesTitle)}
                value={values.notes_title ?? ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.notes_title && errors.notes_title
                    ? (errors.notes_title as string)
                    : undefined
                }
              />
              <div className="flex flex-col gap-1.5">
                <Text
                  variant="label1"
                  as="label"
                  htmlFor="training-notes"
                  className="text-ds-text-secondary font-medium"
                >
                  {i18n.ft(messages.notesDescription)}
                </Text>
                <RichTextEditor
                  id="training-notes"
                  className="w-full [&_.RichTextEditor]:max-w-none [&_.ql-container]:min-h-[150px]"
                  value={values.notes || ''}
                  onChange={(value: string) => setFieldValue('notes', value)}
                  onBlur={() => setFieldTouched('notes', true)}
                  hasError={Boolean(touched.notes && errors.notes)}
                />
                {touched.notes && errors.notes ? (
                  <Text
                    variant="caption"
                    className="text-ds-text-negative mt-1"
                  >
                    {errors.notes as string}
                  </Text>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              shape="rounded"
              onClick={onCancel}
              disabled={submitting}
            >
              {i18n.ft(messages.cancel)}
            </Button>
            <div className="inline-flex rounded shadow-sm">
              <Button
                type="submit"
                shape="rounded"
                loading={submitting}
                startIcon={!isValid ? AlertTriangleFilled : undefined}
                className={!edit ? '!rounded-r-none' : undefined}
              >
                {submitLabel}
              </Button>

              {!edit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      disabled={submitting}
                      className="inline-flex items-center justify-center px-3.5 py-2.5 bg-ds-action-primary-resting text-ds-text-aux hover:bg-ds-action-primary-hover hover:text-ds-text-aux border-l border-ds-white rounded-r transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-fuschia-10"
                      aria-label={i18n.ft(messages.additionalSubmitOptions)}
                    >
                      <CaretDown size={14} className="text-white fill-white" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="bg-white shadow-lg border border-gray-200 min-w-[180px] p-1.5"
                    onCloseAutoFocus={event => event.preventDefault()}
                  >
                    {submitOptions(setFieldValue, submitForm).map(option => (
                      <DropdownMenuItem
                        key={option.key}
                        onSelect={option.onClick}
                        className="cursor-pointer px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded"
                      >
                        {option.text}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}

TrainingForm.propTypes = {
  edit: PropTypes.bool,
  training: PropTypes.object,
  template: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  submitLabel: PropTypes.string.isRequired
};

export default TrainingForm;
